import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import find from "lodash/find";
import get from "lodash/get";
import map from "lodash/map";
import toLower from "lodash/toLower";
import { getLast2Snapshots } from "actions/ProviderActions";
import GenericGameCard from "components/games/elements/GenericGameCard";
import SpinnerTakeover from "components/common/SpinnerTakeover";

// settings animationOptions notes
//      animationOptions: [{
//          animationDuration: 100 * number of frames - without this, the animation will play forever
//          duration: 100 * number fo frames - without this, the animation will NOT play
//          frameColumns: number of columns in the image
//          frameCount: total number of frames
//          height: the height of a single frame
//          width: the width of a single frame
//      }]

function DefaultGameCard(props) {
    const {
        clinicId = null,
        game= {},
        getSnapshots = () => undefined,
        legalWordingFilePath= undefined,
        settings = {},
        snapshots = [],
    } = props;

    useEffect(() => {
        if (game?.greenlineGameId && game?.daysFromStart !== 0) {
            getSnapshots(game.greenlineGameId, game.ownerUserId, clinicId, false);
        }
    }, [game]);

    const getFrames = (animation) => {
        let framesList = [];
        for (let i = 0; i < animation.frameCount; i++) {
            const row = Math.floor(i / animation.frameColumns);
            const frameRows = Math.ceil(animation.frameCount / animation.frameColumns);
            framesList.push({
                height: animation.imageHeight,
                left: `-${((animation.imageWidth / animation.frameColumns) * i) % animation.imageWidth}px`,
                top: `-${row * (animation.imageHeight / frameRows)}px`,
                width: animation.imageWidth,
            });
        }
        return framesList;
    };

    const animationOptions = useMemo(() => {
        return map(settings.animationOptions, ao => ({
                ...ao,
                animations: map(ao.animations, a => ({
                    ...a,
                    frames: getFrames(a),
                })),
            })
        );
    }, [settings]);

    if (game?.daysFromStart > 0 && !snapshots?.length) {
        return <SpinnerTakeover show />;
    }

    const handleHasScored = () => {
        let today;
        let yesterday;
        if (snapshots?.length === 1) {
            // Handle when it's the first day, all 0s for Yesterday
            yesterday = snapshots[0];
            today = find(game.data.Clinics, clinic => clinic.ClinicId === clinicId);
        } else if (snapshots?.length) {
            yesterday = find(snapshots[0].data.Clinics, clinic => clinic.ClinicId === clinicId);
            today = find(snapshots[1].data.Clinics, clinic => clinic.ClinicId === clinicId);
        } else {
            return false;
        }
        const todayTotalDoses = get(today, "CurrentPeriod.TotalReportDoses", 0);
        const yesterdayTotalDoses = get(yesterday, "CurrentPeriod.TotalReportDoses", 0);
        const newDoses = todayTotalDoses - yesterdayTotalDoses;
        return newDoses > 0;
    };

    const handleGetAnimationIndex = () => {
        const clinicDetails = find(game.details, { ClinicId: props.clinicId });
        const currentAnimation = find(settings.animationIndexes, {
            avatarType: toLower(clinicDetails?.AvatarType) || toLower(clinicDetails?.avatarType),
            hasScored: handleHasScored(),
        });
        return currentAnimation?.animationIndex || 0;
    };

    return (
        <GenericGameCard
            {...props}
            animationOptions={animationOptions}
            bannerImage={settings.bannerImage}
            bannerImageAltText={settings.bannerImageAltText}
            legalWordingFilePath={legalWordingFilePath}
            getAnimationIndex={handleGetAnimationIndex}
            points
            scoreStyles={{
                fontSize: "2em",
                right: "2em",
                top: "1em",
            }}
            snapshotsAlreadyLoaded
        />
    );
}

DefaultGameCard.propTypes = {
    clinicId: PropTypes.number,
    game: PropTypes.object,
    legalWordingFilePath: PropTypes.string,
    nodeId: PropTypes.number,
    settings: PropTypes.object,
    snapshot: PropTypes.bool,
    userId: PropTypes.number,
};

export default connect(
    (state, ownProps) => ({
        snapshots: state.entities.gameSnapshotComparison?.[ownProps.game?.greenlineGameId] || undefined,
    }),
    (dispatch) => ({
        getSnapshots: (gameId, userId, clinicId, isDemo) => dispatch(getLast2Snapshots(gameId, userId, clinicId, isDemo)),
    })
)(DefaultGameCard);
